import { MetadataField } from '@/api/generatedApi';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { Ellipsis } from '@/components/Ellipsis';
import { CreateMetadataFieldModal } from '@/components/modals/CreateMetadataFieldModal';
import { DeleteMetadataFieldModal } from '@/components/modals/DeleteMetadataFieldModal';
import { EditMetadataFieldModal } from '@/components/modals/EditMetadataFieldModal';
import { MetadataTestIds } from '@/enums/metadataTestIds.enum';
import { useFetchListMetadataFields } from '@/hooks/api/metadata/useFetchListMetadatFields';
import { useContextMenu } from '@/hooks/useContextMenu';
import { CopiableGridColDef } from '@/types/copiableColumn';
import { dateFormat } from '@/utils/format';
import { getCreatorEmailValue } from '@/utils/dataGridValueGetters';
import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Tooltip } from '@mui/material';
import { GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { RenderCreator } from '../ContentsTable/cellDefinitions';

const ActionsCell = (parameters: GridRenderCellParams<MetadataField>) => {
  const [selectedField, setSelectedField] = useState<MetadataField>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleOpenEditModal = () => {
    setSelectedField(parameters.row);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedField(undefined);
  };

  const handleOpenDeleteModal = () => {
    setSelectedField(parameters.row);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedField(undefined);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
        <IconButton
          onClick={handleOpenEditModal}
          size='small'
          data-testid={`${MetadataTestIds.MetadataEditButton}-${parameters.row.id}`}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={handleOpenDeleteModal}
          size='small'
          data-testid={`${MetadataTestIds.MetadataDeleteButton}-${parameters.row.id}`}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
      {selectedField && (
        <>
          <EditMetadataFieldModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            metadataField={selectedField}
            onSuccess={(newEntry: MetadataField) => {
              parameters.api.updateRows([
                {
                  ...parameters.row,
                  ...newEntry,
                },
              ]);
              handleCloseEditModal();
            }}
          />
          <DeleteMetadataFieldModal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            metadataField={selectedField}
            onSuccess={() => {
              parameters.api.updateRows([
                {
                  ...parameters.row,
                  _action: 'delete',
                },
              ]);
              handleCloseDeleteModal();
            }}
          />
        </>
      )}
    </>
  );
};

const columns: CopiableGridColDef<MetadataField>[] = [
  {
    field: 'name',
    headerName: 'Key Name',
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<MetadataField>) => <Ellipsis text={parameters.row.name} />,
    getCopyValue: row => row.name,
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.1,
    minWidth: 75,
    sortable: false,
    filterable: false,
    getCopyValue: row => row.type,
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    flex: 0.15,
    minWidth: 100,
    sortable: false,
    filterable: false,
    renderCell: parameters => dateFormat(parameters.row.createdAt),
    getCopyValue: row => dateFormat(row.createdAt),
  },
  {
    field: 'creator',
    headerName: 'Created By',
    flex: 0.15,
    minWidth: 200,
    sortable: false,
    filterable: false,
    renderCell: RenderCreator,
    valueGetter: getCreatorEmailValue,
    getCopyValue: row => row.creator?.email,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 0.1,
    minWidth: 90,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<MetadataField>) => <ActionsCell {...parameters} />,
  },
];

type CreateFieldToolbarProperties = {
  onCreateClick: () => void;
};

declare module '@mui/x-data-grid-pro' {
  // eslint-disable-next-line unicorn/prevent-abbreviations, @typescript-eslint/no-empty-object-type -- MUI's interface name, must use extends for module augmentation
  interface ToolbarPropsOverrides extends CreateFieldToolbarProperties {}
}

const CreateFieldToolbar = ({ onCreateClick }: CreateFieldToolbarProperties) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px 6px 8px' }}>
    <Tooltip title='Create field' placement='bottom'>
      <IconButton
        size='small'
        onClick={onCreateClick}
        data-testid={MetadataTestIds.CreateMetadataFieldButton}
        sx={{
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Add fontSize='small' />
      </IconButton>
    </Tooltip>
  </Box>
);

export const MetadataKeysTable = () => {
  const { fetchData, error } = useFetchListMetadataFields();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const gridApi = useGridApiRef();

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<MetadataField>({
    canUseContextMenu: true,
    getRowById: (rowId: string | number) => gridApi.current?.getRow<MetadataField>(rowId) ?? undefined,
  });

  return (
    <Box onContextMenu={handleGridContextMenu} sx={{ height: '100%' }}>
      <DataGridLazy<MetadataField, { metadataFields: MetadataField[] }>
        apiRef={gridApi}
        columns={columns}
        fetchRows={fetchData}
        dataKey='metadataFields'
        rowHeight={80}
        error={error}
        density='compact'
        hideFooter
        disableColumnFilter
        disableColumnMenu
        showToolbar
        localeText={{ noRowsLabel: 'No metadata fields' }}
        sx={{
          height: '100%',
          width: '100%',
          borderRadius: '6px',
        }}
        slotProps={{
          root: { 'data-testid': MetadataTestIds.MetadataTable },
          toolbar: { onCreateClick: () => setIsCreateModalOpen(true) },
        }}
        slots={{
          toolbar: CreateFieldToolbar,
        }}
      />
      <CreateMetadataFieldModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      {getCopyValueFromColumn(columns, contextMenuClickedItem, contextMenuClickedField) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          <CopyMenuItem
            columns={columns}
            row={contextMenuClickedItem}
            field={contextMenuClickedField}
            onCopy={handleCloseContextMenu}
          />
        </BaseContextMenu>
      )}
    </Box>
  );
};
